/**
 * StatusBar Component
 *
 * Bottom status bar showing:
 * - Save status
 * - Lock info
 * - Current environment
 * - Config version
 * - Line/column position
 */

import {
  Save,
  Lock,
  Unlock,
  CheckCircle,
  AlertCircle,
  Loader2,
  GitBranch,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";
import type { ReactElement } from "react";

import { useAuthStore } from "../../store/authStore";
import { useConfigStore } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import { ENVIRONMENT_LABELS, CONFIG_STATUS_INFO } from "../../types";

interface StatusBarProps {
  readonly sidebarVisible: boolean;
  readonly onToggleSidebar: () => void;
}

export function StatusBar({ sidebarVisible, onToggleSidebar }: StatusBarProps): ReactElement {
  const { saveStatus, cursorPosition, isDirty } = useEditorStore();
  const { metadata, currentPath } = useConfigStore();
  const { environment } = useAuthStore();

  return (
    <footer className="flex h-6 items-center justify-between border-t border-border bg-background-secondary px-2 text-xs">
      {/* Left side */}
      <div className="flex items-center gap-3">
        {/* Sidebar toggle */}
        <button
          onClick={onToggleSidebar}
          className="flex items-center text-foreground-muted hover:text-foreground"
          title={sidebarVisible ? "Hide Sidebar" : "Show Sidebar"}
        >
          {sidebarVisible ? (
            <PanelLeftClose className="h-3.5 w-3.5" />
          ) : (
            <PanelLeft className="h-3.5 w-3.5" />
          )}
        </button>

        {/* Save Status */}
        <SaveStatusIndicator status={saveStatus} isDirty={isDirty} />

        {/* Lock Status */}
        {currentPath && (
          <LockStatusIndicator
            isLocked={metadata?.status === "live"}
            lockedBy={metadata?.updatedBy}
          />
        )}

        {/* Config Status */}
        {metadata && (
          <span
            className={`flex items-center gap-1 ${
              CONFIG_STATUS_INFO[metadata.status].color === "success"
                ? "text-success"
                : CONFIG_STATUS_INFO[metadata.status].color === "warning"
                  ? "text-warning"
                  : "text-foreground-muted"
            }`}
          >
            {CONFIG_STATUS_INFO[metadata.status].label}
          </span>
        )}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* Version */}
        {metadata && (
          <span className="flex items-center gap-1 text-foreground-muted">
            <GitBranch className="h-3 w-3" />
            <span>v{metadata.version}</span>
          </span>
        )}

        {/* Cursor Position */}
        {cursorPosition && (
          <span className="text-foreground-muted">
            Ln {cursorPosition.line}, Col {cursorPosition.column}
          </span>
        )}

        {/* Environment */}
        <span className="flex items-center gap-1 text-foreground-muted">
          <span
            className={`h-2 w-2 rounded-full ${
              environment === "prod"
                ? "bg-error"
                : environment === "staging"
                  ? "bg-warning"
                  : "bg-success"
            }`}
          />
          <span>{ENVIRONMENT_LABELS[environment]}</span>
        </span>
      </div>
    </footer>
  );
}

interface SaveStatusIndicatorProps {
  readonly status: "idle" | "saving" | "saved" | "error";
  readonly isDirty: boolean;
}

function SaveStatusIndicator({ status, isDirty }: SaveStatusIndicatorProps): ReactElement {
  switch (status) {
    case "saving":
      return (
        <span className="flex items-center gap-1 text-primary">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Saving...</span>
        </span>
      );
    case "saved":
      return (
        <span className="flex items-center gap-1 text-success">
          <CheckCircle className="h-3 w-3" />
          <span>Saved</span>
        </span>
      );
    case "error":
      return (
        <span className="flex items-center gap-1 text-error">
          <AlertCircle className="h-3 w-3" />
          <span>Save failed</span>
        </span>
      );
    default:
      return (
        <span className="flex items-center gap-1 text-foreground-muted">
          <Save className="h-3 w-3" />
          <span>{isDirty ? "Unsaved changes" : "No changes"}</span>
        </span>
      );
  }
}

interface LockStatusIndicatorProps {
  readonly isLocked: boolean;
  readonly lockedBy: string | undefined;
}

function LockStatusIndicator({ isLocked, lockedBy }: LockStatusIndicatorProps): ReactElement {
  if (isLocked) {
    return (
      <span
        className="flex items-center gap-1 text-warning"
        title={lockedBy ? `Locked by ${lockedBy}` : "Read-only (Live)"}
      >
        <Lock className="h-3 w-3" />
        <span>Read-only</span>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1 text-foreground-muted">
      <Unlock className="h-3 w-3" />
      <span>Editable</span>
    </span>
  );
}
